import _ from "lodash";
import React, { ReactElement, useEffect, useMemo, useState } from "react";
import { DataTable, Loading } from "../ui";
import DataTableEditableCell from "../ui/DataTableEditableCell";
import { Form } from "./Form";
import { FormService } from "./FormService";

import { Button, Card, Form as BSForm } from "react-bootstrap";
import { FormCreateForm } from "./FormCreateForm";

const FORM_TYPE_OPTIONS = [
	{ value: "courseware", label: "Courseware" },
	{ value: "elearning", label: "eLearning" },
	{ value: "virtualcampus", label: "Virtual Campus" },
	{ value: "dmi", label: "DMI" },
	{ value: "gameo", label: "Gameo" },
	{ value: "whitepaper", label: "Whitepaper" },
	{ value: "generic", label: "Generic" },
];

const TYPE_COLORS: Record<string, { bg: string; text: string }> = {
	courseware: { bg: "#4186e0", text: "#fff" },
	elearning: { bg: "#6558ee", text: "#fff" },
	virtualcampus: { bg: "#37c5ab", text: "#fff" },
	dmi: { bg: "#fd612c", text: "#fff" },
	gameo: { bg: "#a4cf30", text: "#1a1a2e" },
	whitepaper: { bg: "#eec300", text: "#1a1a2e" },
	generic: { bg: "#8da3a6", text: "#fff" },
};

const GuidCell = (props: any) => {
	const [copied, setCopied] = useState(false);
	const guid = props.cell.value;

	const handleCopy = () => {
		navigator.clipboard.writeText(guid);
		setCopied(true);
		setTimeout(() => setCopied(false), 1500);
	};

	return (
		<span className="forms-guid-cell" onClick={handleCopy} title="Click to copy">
			<code>{guid}</code>
			<i className={`fa ${copied ? "fa-check" : "fa-clipboard"}`} />
		</span>
	);
};

const DataTableSelectCell = (props: any) => {
	const [value, setValue] = useState(props.cell.value);

	useEffect(() => {
		setValue(props.cell.value);
	}, [props.cell.value]);

	const onChange = (e: any) => {
		setValue(e.target.value);
		props.cell.column.onUpdate(props.row.index, e.target.value);
	};

	return (
		<BSForm.Control as="select" value={value} onChange={onChange}>
			{FORM_TYPE_OPTIONS.map(opt => (
				<option key={opt.value} value={opt.value}>
					{opt.label}
				</option>
			))}
		</BSForm.Control>
	);
};

export const FormAdminPage = (): ReactElement => {
	const [forms, setForms] = useState<Form[]>();
	const [showCreate, setShowCreate] = useState(false);
	const [typeFilter, setTypeFilter] = useState<string | null>(null);
	const [flash, setFlash] = useState<string | null>(null);

	const formService = new FormService();

	useMemo(() => {
		const fetchForms = async () => {
			const formData = await formService.findAll();
			setForms(formData);
		};

		fetchForms();
	}, []);

	if (!forms) {
		return <Loading />;
	}

	const showFlash = (msg: string) => {
		setFlash(msg);
		setTimeout(() => setFlash(null), 3000);
	};

	const onUpdateField = (index: number, field: string, value: string) => {
		const newForms = [...filteredForms];
		_.set(newForms, `[${index}].${field}`, value);
		formService.save(newForms[index]);

		// Update the main forms array too
		const allForms = forms.map(f =>
			f.id === newForms[index].id ? newForms[index] : f,
		);
		setForms(allForms);
		showFlash("Form updated");
	};

	const filteredForms = typeFilter
		? forms.filter(f => f.type === typeFilter)
		: forms;

	// Count forms by type
	const typeCounts: Record<string, number> = {};
	forms.forEach(f => {
		typeCounts[f.type] = (typeCounts[f.type] || 0) + 1;
	});

	const columns = [
		{
			Header: "ID",
			accessor: "id",
			width: "5%",
			disableFilters: true,
		},
		{
			Header: "GUID",
			accessor: "guid",
			width: "25%",
			Cell: GuidCell,
			disableFilters: true,
		},
		{
			Header: "Name",
			accessor: "name",
			width: "40%",
			Cell: DataTableEditableCell,
			onUpdate: (index: number, value: string) => {
				onUpdateField(index, "name", value);
			},
		},
		{
			Header: "Type",
			accessor: "type",
			width: "30%",
			Cell: DataTableSelectCell,
			disableFilters: true,
			onUpdate: (index: number, value: string) => {
				onUpdateField(index, "type", value);
			},
		},
	];

	return (
		<div className="container mb-3" id="forms-admin">
			{/* Page header */}
			<div className="forms-page-header">
				<div className="forms-page-header-left">
					<div className="forms-page-icon">
						<i className="fa fa-file-alt" />
					</div>
					<div>
						<h1>Forms</h1>
						<span className="forms-page-count">
							{forms.length} form{forms.length !== 1 ? "s" : ""}
						</span>
					</div>
				</div>
				<Button
					variant={showCreate ? "outline-secondary" : "primary"}
					onClick={() => setShowCreate(!showCreate)}
					className="forms-create-toggle"
				>
					<i className={`fa ${showCreate ? "fa-times" : "fa-plus"}`} />{" "}
					{showCreate ? "Cancel" : "New Form"}
				</Button>
			</div>

			{/* Flash message */}
			{flash && (
				<div className="forms-flash">
					<i className="fa fa-check-circle" /> {flash}
				</div>
			)}

			{/* Create form (collapsible) */}
			{showCreate && (
				<Card className="mb-4 forms-create-card">
					<Card.Body>
						<FormCreateForm
							onCreate={form => {
								const updated = [form, ...forms];
								setForms(updated);
								setShowCreate(false);
								showFlash(`"${form.name}" created`);
							}}
						/>
					</Card.Body>
				</Card>
			)}

			{/* Type filter pills */}
			<div className="forms-type-filters">
				<button
					className={`forms-filter-pill ${typeFilter === null ? "active" : ""}`}
					onClick={() => setTypeFilter(null)}
				>
					All <span className="forms-filter-count">{forms.length}</span>
				</button>
				{FORM_TYPE_OPTIONS.map(opt => {
					const count = typeCounts[opt.value] || 0;
					if (count === 0) return null;
					const colors = TYPE_COLORS[opt.value] || TYPE_COLORS.generic;
					return (
						<button
							key={opt.value}
							className={`forms-filter-pill ${typeFilter === opt.value ? "active" : ""}`}
							onClick={() =>
								setTypeFilter(
									typeFilter === opt.value ? null : opt.value,
								)
							}
							style={
								typeFilter === opt.value
									? { background: colors.bg, color: colors.text, borderColor: colors.bg }
									: {}
							}
						>
							{opt.label}{" "}
							<span className="forms-filter-count">{count}</span>
						</button>
					);
				})}
			</div>

			{/* Data table */}
			<div className="editable-data-table forms-table-wrapper">
				<DataTable columns={columns} data={filteredForms} />
			</div>
		</div>
	);
};
